import { ColorParams, StrokeType, TextDecoration, TextScript, TextTransform } from '@bynder-studio/misc';
import {
    type BoundedTextProps,
    getFontById,
    getStructuredText,
    HorizontalAlignment,
    type IStructuredGlyph,
    Placement,
    type StyleProps,
    type TextProps,
    TextRenderer,
    VerticalAlignment,
} from '@bynder-studio/structured-text';
import type { IAssetsLoader } from '../../AssetLoader/IAssetsLoader';
import type { IAsset } from '../Assets/IAsset';
import { FontAsset } from '../Assets/FontAsset';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { BaseProperty } from './BaseProperty';
import {
    type TranscriptBackground,
    type TranscriptCue,
    type TranscriptElementParams,
    type TranscriptStroke,
} from '../../types';
import { GroupCompElement } from '../CompModels/Elements/GroupCompElement';
import { ShapeCompElement } from '../CompModels/Elements/ShapeCompElement';
import { Box } from '../Shared/Box';
import { Position } from '../Shared/Position';
import { Dimension } from '../Shared/Dimension';
import { createRectanglePath } from '../../Helpers/pathFunctions';
import { SvgCompElement } from '../CompModels/Elements/SvgCompElement';
import { srtTimecodeToFrame } from '../../Helpers/transcriptTimecode';

// When FFmpeg converts SRT to ASS internally, it uses these PlayRes values
// and style defaults. All ASS coordinates are in PlayRes units and scale
// to actual video pixels via: px = value × (videoDimension / playRes).
const LIBASS_PLAY_RES_X = 384;
const LIBASS_PLAY_RES_Y = 288;
const LIBASS_FONT_SIZE = 16; // PlayRes units
const LIBASS_OUTLINE = 1; // PlayRes units  (BorderStyle=1)
const LIBASS_SHADOW = 0; // PlayRes units
const LIBASS_MARGIN_L = 20; // PlayRes units
const LIBASS_MARGIN_R = 20; // PlayRes units
const LIBASS_MARGIN_V = 20; // PlayRes units
// Alignment 2 = bottom-center (SSA numpad convention)

/** Primary fill colour – white, fully opaque */
const LIBASS_PRIMARY_COLOR = {
    type: 'SOLID' as const,
    red: 255,
    green: 255,
    blue: 255,
    opacity: 1,
    brandColorId: null,
};

/** Outline colour – black, fully opaque */
const LIBASS_STROKE_COLOR = { type: 'SOLID' as const, red: 0, green: 0, blue: 0, opacity: 1, brandColorId: null };

/**
 * Compute pixel-accurate libass transcript metrics from the actual video size.
 *
 * font_size_px   = LIBASS_FONT_SIZE × (videoHeight / LIBASS_PLAY_RES_Y)
 * outline_px     = LIBASS_OUTLINE   × (videoHeight / LIBASS_PLAY_RES_Y)
 * marginL_px     = LIBASS_MARGIN_L  × (videoWidth  / LIBASS_PLAY_RES_X)
 * marginR_px     = LIBASS_MARGIN_R  × (videoWidth  / LIBASS_PLAY_RES_X)
 * marginV_px     = LIBASS_MARGIN_V  × (videoHeight / LIBASS_PLAY_RES_Y)
 * maxLineWidth   = videoWidth - marginL_px - marginR_px
 */
function computeLibassMetrics(videoWidth: number, videoHeight: number) {
    const scaleX = videoWidth / LIBASS_PLAY_RES_X;
    const scaleY = videoHeight / LIBASS_PLAY_RES_Y;

    return {
        fontSize: LIBASS_FONT_SIZE * scaleY,
        outline: LIBASS_OUTLINE * scaleY,
        shadow: LIBASS_SHADOW * scaleY,
        marginL: LIBASS_MARGIN_L * scaleX,
        marginR: LIBASS_MARGIN_R * scaleX,
        marginV: LIBASS_MARGIN_V * scaleY,
        maxLineWidth: videoWidth - LIBASS_MARGIN_L * scaleX - LIBASS_MARGIN_R * scaleX,
        boxHeight: videoHeight - LIBASS_MARGIN_V * scaleY,
    };
}

export const defaultTranscriptElementParams: Partial<TranscriptElementParams> = {
    id: Date.now() - 777 ** 3,
    name: 'DEFAULT_TRANSCRIPT_NAME',
};

export class TranscriptElement extends BaseProperty {
    enabled = false;

    cues: TranscriptCue[] = [];

    fontId: string | null = null;

    fontSize = 0;

    color: ColorParams = LIBASS_PRIMARY_COLOR;

    background: TranscriptBackground = {
        enabled: false,
        color: {
            type: 'SOLID' as const,
            red: 0,
            green: 0,
            blue: 0,
            opacity: 1,
            brandColorId: null,
        },
    };

    stroke: TranscriptStroke = {
        enabled: false,
        type: StrokeType.CENTER,
        color: LIBASS_STROKE_COLOR,
    };

    dimension: Dimension = new Dimension(8, 0);

    position: Position = new Position(0, 0);

    assetLoader: IAssetsLoader | null = null;

    isAssetLoading = false;

    readonly opacity = 100;

    readonly rotation = 0;

    /** Computed once from canvas dimensions at construction time; not serialized
     *  because it's derived from the page size which never changes. */
    private _metrics: ReturnType<typeof computeLibassMetrics> | null = null;

    /** Tracks whether font size was explicitly set by input params. */
    private _hasCustomFontSize = false;

    /** Persisted slot assignments for libass-style collision detection.
     *  Slot 0 = bottom-most position, slot 1 = one row above, etc.
     *  Existing cues keep their slot; new cues take the lowest free slot. */
    private readonly _slotMap: Map<string, number> = new Map();

    /** Cached formattedText (TextProps) per cue text. Invalidated when style changes. */
    private _formattedTextCache: Map<string, TextProps> = new Map();

    /** Cached comp element per active cue combination. Invalidated when style changes. */
    private _compElementCache: Map<string, GroupCompElement> = new Map();

    /** Derived per-cue frame boundaries, computed from each cue's SRT timecode + frameRate.
     *  Rebuilt lazily only when the cues or the frame rate change, so the render hot path
     *  (getActiveCues) stays a pure integer comparison with no per-frame timecode parsing. */
    private _frameBounds: Array<{ startFrame: number; endFrame: number }> = [];

    private _frameBoundsFrameRate: number | null = null;

    private computeLibassMetrics(canvasDimension?: Dimension) {
        if (canvasDimension) {
            this._metrics = computeLibassMetrics(canvasDimension.getWidth(), canvasDimension.getHeight());
        }
    }

    private getMetrics() {
        if (!this._metrics) {
            this._metrics = computeLibassMetrics(this.dimension.getWidth(), this.dimension.getHeight());
        }

        return this._metrics;
    }

    setCanvasDimension(canvasDimension?: Dimension): void {
        const prevMetrics = this._metrics;
        this.computeLibassMetrics(canvasDimension);

        const metrics = this._metrics;

        if (!metrics) {
            return;
        }

        if (!this._hasCustomFontSize) {
            this.fontSize = metrics.fontSize;
        }

        const metricsChanged =
            !prevMetrics ||
            prevMetrics.fontSize !== metrics.fontSize ||
            prevMetrics.outline !== metrics.outline ||
            prevMetrics.shadow !== metrics.shadow ||
            prevMetrics.marginL !== metrics.marginL ||
            prevMetrics.marginR !== metrics.marginR ||
            prevMetrics.marginV !== metrics.marginV ||
            prevMetrics.maxLineWidth !== metrics.maxLineWidth ||
            prevMetrics.boxHeight !== metrics.boxHeight;

        if (metricsChanged) {
            this._formattedTextCache.clear();
            this._compElementCache.clear();
        }
    }

    setAssetLoader(loader: IAssetsLoader): void {
        this.assetLoader = loader;
    }

    constructAsset(): void {
        if (!this.assetLoader) {
            return;
        }

        const url = getFontById(this.fontId)?.url;
        const textRenderer = TextRenderer.getInstance();

        const inList = this.assetLoader.getAsset(this.fontId);
        const asset = new FontAsset({
            id: this.fontId,
            fontId: Number(this.fontId),
            src: url,
        });
        this.isAssetLoading = true;
        this.assetLoader.setAsset(asset);
        this.assetLoader
            .loadFont(asset)
            .then((loadedAsset) => {
                if (loadedAsset.object && !inList) {
                    textRenderer.setFont(this.fontId, loadedAsset.object);
                }

                // Invalidate cached formattedText so glyphs are re-shaped with loaded font
                this._formattedTextCache.clear();
                this._compElementCache.clear();
            })
            .finally(() => {
                this.isAssetLoading = false;
            });
    }

    isContainsAsset(asset: IAsset): boolean {
        return asset instanceof FontAsset && String(asset.fontId) === String(this.fontId);
    }

    constructor(params: Partial<TranscriptElementParams> = {}, canvasDimension?: Dimension) {
        super();
        this.setCanvasDimension(canvasDimension);
        this.setProperties(params);
    }

    update(params: Partial<TranscriptElementParams>): Set<ElementUpdateTypes> {
        const updateTypes = this.setProperties(params);

        // Invalidate caches and reload font assets when relevant properties change
        if (
            params.cues !== undefined ||
            params.enabled !== undefined ||
            params.fontId !== undefined ||
            params.fontSize !== undefined ||
            params.color !== undefined ||
            params.background !== undefined ||
            params.stroke !== undefined
        ) {
            // Clear caches since style or content changed
            this._formattedTextCache.clear();
            this._compElementCache.clear();

            if (this.cues.length > 0) {
                this.constructAsset();
            }
        }

        return updateTypes;
    }

    setProperties(params: Partial<TranscriptElementParams>): Set<ElementUpdateTypes> {
        const updateTypes: Set<ElementUpdateTypes> = super.setProperties(params);

        if (params.enabled !== undefined) {
            this.enabled = params.enabled;
            updateTypes.add(ElementUpdateTypes.ENABLED);
        }

        if (params.cues !== undefined) {
            this.cues = params.cues;
            // Invalidate derived frame boundaries so they are recomputed from the new cues.
            this._frameBounds = [];
            this._frameBoundsFrameRate = null;
            updateTypes.add(ElementUpdateTypes.CUES);
        }

        if (params.fontId !== undefined) {
            this.fontId = String(params.fontId);
            updateTypes.add(ElementUpdateTypes.FONT_FAMILY);
        }

        if (params.fontSize !== undefined) {
            this.fontSize = params.fontSize;
            this._hasCustomFontSize = true;
            updateTypes.add(ElementUpdateTypes.FONT_SIZE);
        }

        if (params.color !== undefined) {
            this.color = params.color;
            updateTypes.add(ElementUpdateTypes.FILL_COLOR);
        }

        if (params.background !== undefined) {
            this.background = params.background;
            updateTypes.add(ElementUpdateTypes.TRANSCRIPT_BACKGROUND_COLOR);
        }

        if (params.stroke !== undefined) {
            this.stroke = params.stroke;
            updateTypes.add(ElementUpdateTypes.TRANSCRIPT_STROKE);
        }

        if (params.position !== undefined && params.position !== null) {
            this.position = new Position(params.position.x, params.position.y);
            updateTypes.add(ElementUpdateTypes.POSITION);
        }

        if (params.dimension !== undefined && params.dimension !== null) {
            this.dimension = new Dimension(params.dimension.width, params.dimension.height);
            updateTypes.add(ElementUpdateTypes.DIMENSION);
        }

        if (!this.fontSize && this._metrics) {
            // Setting default fontSize based on video dimensions if not already set
            this.fontSize = this._metrics.fontSize;
        }

        return updateTypes;
    }

    /**
     * Derive (and memoize) the frame boundary for each cue from its canonical SRT
     * timecode. Rebuilt only when cues or the frame rate change, keeping the render
     * hot path a pure integer comparison.
     */
    private ensureFrameBounds(frameRate: number): Array<{ startFrame: number; endFrame: number }> {
        if (this._frameBounds && this._frameBoundsFrameRate === frameRate) {
            return this._frameBounds;
        }

        this._frameBounds = this.cues.map((cue) => ({
            startFrame: srtTimecodeToFrame(cue.startTime, frameRate),
            endFrame: srtTimecodeToFrame(cue.endTime, frameRate),
        }));
        this._frameBoundsFrameRate = frameRate;

        return this._frameBounds;
    }

    getActiveCues(frameIndex: number, frameRate: number): TranscriptCue[] {
        if (!this.enabled) {
            return [];
        }

        const bounds = this.ensureFrameBounds(frameRate);

        return this.cues.filter((_, i) => frameIndex >= bounds[i].startFrame && frameIndex < bounds[i].endFrame);
    }

    // TODO: Could be simplified later since we know which format we are using
    private buildFormattedText(cueText: string): TextProps {
        const cached = this._formattedTextCache.get(cueText);

        if (cached) {
            return cached;
        }

        const metrics = this.getMetrics();

        const styleProps: StyleProps = {
            fontId: String(this.fontId),
            fontSize: this.fontSize,
            styleId: null,
            color: this.color,
            tracking: 0,
            leading: 1.0,
            textTransform: TextTransform.NONE,
            textDecoration: TextDecoration.NONE,
            textScript: TextScript.BASE,
            stroke: {
                type: StrokeType.CENTER,
                width: metrics.outline,
            },
            paragraphSpacing: 0,
        };

        const result = getStructuredText(cueText, styleProps);
        this._formattedTextCache.set(cueText, result);

        return result;
    }

    getCompElement(frameIndex: number, frameRate: number): GroupCompElement {
        const activeCues = this.getActiveCues(frameIndex, frameRate);

        if (activeCues.length === 0) {
            return null;
        }

        // libass collision detection: existing cues keep their slot,
        // new cues take the lowest available slot.
        const activeCueIds = new Set(activeCues.map((c) => c.id));

        // Remove slots for cues that are no longer active
        for (const cueId of this._slotMap.keys()) {
            if (!activeCueIds.has(cueId)) {
                this._slotMap.delete(cueId);
            }
        }

        // Assign slots for new cues (lowest free slot)
        const usedSlots = new Set(this._slotMap.values());

        for (const cue of activeCues) {
            if (!this._slotMap.has(cue.id)) {
                let slot = 0;

                while (usedSlots.has(slot)) {
                    slot++;
                }

                this._slotMap.set(cue.id, slot);
                usedSlots.add(slot);
            }
        }

        // Order cues by slot (highest slot = topmost line, slot 0 = bottom line)
        const cuesBySlot = activeCues
            .map((cue) => ({ cue, slot: this._slotMap.get(cue.id) ?? 0 }))
            .sort((a, b) => b.slot - a.slot);

        // Combine all cue texts into a single string — each cue becomes a line
        const combinedText = cuesBySlot.map(({ cue }) => cue.text).join('\n');
        const combinedId = cuesBySlot.map(({ cue }) => cue.id).join('_');

        // Check cache first to avoid recreating comp elements for the same cue combination
        const cachedCompElement = this._compElementCache.get(combinedId);

        if (cachedCompElement) {
            return cachedCompElement;
        }

        const formattedText = this.buildFormattedText(combinedText);
        const compElement = this.buildCompElement(combinedId, formattedText);

        // Cache the comp element for reuse
        this._compElementCache.set(combinedId, compElement);

        return compElement;
    }

    /**
     * libass WrapStyle=0 ("smart wrapping") — balance line widths.
     *
     * Algorithm (mirrors ass_split_lines in libass):
     *  1. Shape with full maxLineWidth → greedy line count N.
     *  2. If N ≤ 1, nothing to balance → return as-is.
     *  3. Binary-search for the narrowest width that still wraps into
     *     exactly N lines.  The result is a set of lines whose widths
     *     are approximately equal, with the top line slightly wider.
     */
    private createSmartWrappedBoundedText(
        textRenderer: TextRenderer,
        maxWidth: number,
        boxHeight: number,
        formattedText: TextProps,
    ) {
        const makeBounded = (width: number) => {
            const config: BoundedTextProps = {
                textStruct: formattedText,
                boxWidth: width,
                boxHeight,
                placement: Placement.WRAP,
                horizontalAlignment: HorizontalAlignment.CENTER,
                verticalAlignment: VerticalAlignment.BOTTOM,
            };

            return textRenderer.createBoundedText(formattedText, config);
        };

        const countLines = (bt: ReturnType<typeof makeBounded>) => bt.getShapedText().getShapedLines().length;

        // Step 1 – greedy wrap at full width
        const greedyBounded = makeBounded(maxWidth);
        const targetLines = countLines(greedyBounded);

        if (targetLines <= 1) {
            return greedyBounded;
        }

        // Step 2 – binary search for the narrowest width that still fits in targetLines
        let narrowest = 0;
        let widest = maxWidth;
        let bestBounded = greedyBounded;

        // ~20 iterations gives sub-pixel precision for any realistic resolution
        for (let i = 0; i < 20; i++) {
            const mid = (narrowest + widest) / 2;
            const candidate = makeBounded(mid);

            if (countLines(candidate) <= targetLines) {
                // Still fits in targetLines → try narrower
                bestBounded = candidate;
                widest = mid;
            } else {
                // Too narrow → need wider
                narrowest = mid;
            }
        }

        return bestBounded;
    }

    private buildCompElement(cueId: string, formattedText: TextProps): GroupCompElement {
        const metrics = this.getMetrics();

        // Use TextRenderer to shape the transcript text, same as TextElement.init() + constructCompElement()
        const textRenderer = TextRenderer.getInstance();

        // libass Alignment=2 (bottom-center): text wraps within the margin area
        // maxLineWidth = videoWidth - marginL_px - marginR_px
        const boxWidth = metrics.maxLineWidth;
        const boxHeight = metrics.boxHeight;

        // libass WrapStyle=0: smart-wrap to balance line widths
        const boundedText = this.createSmartWrappedBoundedText(textRenderer, boxWidth, boxHeight, formattedText);
        const shapedLines = boundedText.getShapedText().getShapedLines();

        // The smart-wrap may have narrowed the BoundedText box to balance line widths.
        // Glyphs are centered within that (possibly narrower) box.
        // To center them on the full canvas, offset by:
        //   marginL + (fullBoxWidth - actualBoxWidth) / 2
        const [actualBoxWidth] = boundedText.getDimension();
        const boxX = metrics.marginL + (boxWidth - actualBoxWidth) / 2;
        const boxY = 0;

        // Build glyph SvgCompElements — same pattern as TextElement.getSvgCompElement
        const compLines: GroupCompElement[] = [];
        // Capture line metrics from shaped lines for background positioning
        const lineMetricsData: {
            absTop: number;
            absLeft: number;
            width: number;
            ascenderPx: number;
            lineHeightPx: number;
        }[] = [];
        let charIdx = 0;

        shapedLines.forEach((line, lineIdx) => {
            const lineChars: SvgCompElement[] = [];

            line.glyphs.forEach((glyph: IStructuredGlyph) => {
                // Skip newline glyphs — they have no visible path and their degenerate
                // bounding boxes break GroupCompElement position calculations.
                if (glyph.isNewLine() || glyph.data.path === '') {
                    return;
                }

                const compEl = new SvgCompElement();
                compEl.id = `${this.id}_sub_${lineIdx}_${charIdx}`;

                const color = boundedText.getShapedText().getTextPropsProperty('color', glyph.data.runIdx);
                compEl.color = this.stroke.type !== StrokeType.NONE ? this.stroke.color : color;

                compEl.path = glyph.data.path;
                compEl.decorationPaths = glyph.data.decorationPaths;
                compEl.horizontalPathScale = glyph.data.pathScale;
                compEl.verticalPathScale = -1 * glyph.data.pathScale;
                compEl.renderOrder = 99999;
                // libass visually shows only the outer half of the outline (the fill covers the inner half),
                // so we halve it to match the same visual thickness.
                compEl.strokeWidth = this.stroke.type !== StrokeType.NONE ? metrics.outline / 2 : 0;
                compEl.strokeType = this.stroke.type;
                compEl.strokeColor = this.stroke.color;
                compEl.fillColor = color;
                compEl.glyph = glyph.data;

                const absContentBbox = glyph.data.absContentBbox;

                compEl.contentBox = new Box(
                    new Position(0, 0),
                    new Dimension(absContentBbox.width, absContentBbox.height),
                );
                compEl.pathPosition = new Position(
                    glyph.data.absLeftPx - absContentBbox.left,
                    glyph.data.absTopPx - absContentBbox.top,
                );
                compEl.boundingBox = new Box(
                    new Position(boxX + absContentBbox.left, boxY + absContentBbox.top),
                    new Dimension(absContentBbox.width, absContentBbox.height),
                );

                lineChars.push(compEl);
                charIdx++;
            });

            if (lineChars.length > 0) {
                const compLine = new GroupCompElement(lineChars);
                compLine.id = `${this.id}_sub_line_${lineIdx}`;
                compLine.name = `transcript_line_${lineIdx}`;
                compLine.renderOrder = 99999;
                compLines.push(compLine);

                // Store line metrics for background positioning
                // line.absTopPx = baseline - ascenderPx, so baseline = absTopPx + ascenderPx
                lineMetricsData.push({
                    absTop: boxY + line.absTopPx,
                    absLeft: boxX + line.absLeftPx,
                    width: line.metrics.widthPx,
                    ascenderPx: line.metrics.ascenderPx,
                    lineHeightPx: line.metrics.lineHeightPx,
                });
            }
        });

        // Capture line bounding boxes BEFORE creating the text group,
        // because GroupCompElement constructor (makeChildrenRelative) mutates child bounding boxes.
        // Deep copy to preserve absolute positions.
        const lineBoundingBoxes = compLines.map((line) => {
            const bbox = line.boundingBox;

            return new Box(
                new Position(bbox.position.getX(), bbox.position.getY()),
                new Dimension(bbox.dimension.getWidth(), bbox.dimension.getHeight()),
            );
        });

        // Wrap all lines into a text group
        const textGroup = new GroupCompElement(compLines);
        textGroup.id = `${this.id}_sub_text_${cueId}`;
        textGroup.name = `transcript_text_${cueId}`;
        textGroup.renderOrder = 99999;

        // Final group — use default makeChildrenRelative=true so positions cascade correctly.
        // Do NOT override boundingBox/contentBox; let GroupCompElement compute them from children.
        // Build per-line background rectangles (libass BorderStyle=3: opaque box per line,
        // padding = outline size, sharp corners, no configurable radius).
        const groupChildren: (ShapeCompElement | GroupCompElement)[] = [];

        if (this.background.enabled && this.background.color.opacity > 0 && compLines.length > 0) {
            const bgElements = this.buildPerLineBackgroundElements(cueId, lineBoundingBoxes, lineMetricsData);
            groupChildren.push(...bgElements);
        }

        groupChildren.push(textGroup);

        const group = new GroupCompElement(groupChildren);
        group.id = `${this.id}_sub_group_${cueId}`;
        group.name = `transcript_group_${cueId}`;
        group.renderOrder = 99999;
        group.isAssetLoading = this.isAssetLoading;
        group.originalElement = this as any;

        return group;
    }

    /**
     * Build per-line opaque background rectangles behind the transcript text.
     * Matches libass BorderStyle=3 behaviour:
     *  - Each line gets its own rectangle.
     *  - Height = font lineHeight (ascent + descent) + padding * 2.
     *  - Width = line width + padding * 2.
     *  - Padding = outline + shadow (with shadow defaulting to 2 PlayRes units if 0).
     *  - Baseline positioned at ascent + padding from box top for symmetric padding.
     *  - Sharp corners, no border radius.
     */
    private buildPerLineBackgroundElements(
        cueId: string,
        lineBoundingBoxes: Box[],
        lineMetricsData: { absTop: number; absLeft: number; width: number; ascenderPx: number; lineHeightPx: number }[],
    ): ShapeCompElement[] {
        const metrics = this._metrics;
        const outline = metrics?.outline ?? 0;
        // libass applies a default shadow even when Shadow=0 in the style;
        // use a fallback of 2 PlayRes units scaled to pixels for visual padding.
        const DEFAULT_SHADOW_PLAY_RES = 2;
        const scaleY = metrics ? metrics.fontSize / LIBASS_FONT_SIZE : 1;
        const shadow = metrics?.shadow || DEFAULT_SHADOW_PLAY_RES * scaleY;
        const padding = outline + shadow;

        const elements: ShapeCompElement[] = [];

        lineMetricsData.forEach((lineMetrics, idx) => {
            const lineBBox = lineBoundingBoxes[idx];

            if (!lineBBox || lineBBox.dimension.getWidth() === 0) {
                return;
            }

            // Use line metrics for height (ascent + descent from font), not ink bounds
            const lineHeight = lineMetrics.lineHeightPx;
            const ascenderPx = lineMetrics.ascenderPx;

            // Box dimensions
            const w = lineBBox.dimension.getWidth() + padding * 2;
            const h = lineHeight + padding * 2;

            // Position: baseline is at lineMetrics.absTop + ascenderPx
            // For symmetric padding: box top = baseline - ascenderPx - padding
            const baselineY = lineMetrics.absTop + ascenderPx;
            const x = lineBBox.position.getX() - padding;
            const y = baselineY - ascenderPx - padding;

            const compEl = new ShapeCompElement();
            compEl.id = `${this.id}_sub_bg_${cueId}_${idx}`;
            compEl.isAssetLoading = this.isAssetLoading;
            compEl.path = createRectanglePath(0, 0, w, h);
            compEl.color = this.background.color;
            compEl.fillColor = this.background.color;
            compEl.borderColor = this.background.color;
            compEl.borderWidth = 0;
            compEl.horizontalPathScale = 1;
            compEl.verticalPathScale = 1;
            compEl.renderOrder = 99998;
            compEl.pathPosition = new Position(0, 0);
            compEl.contentBox = new Box(new Position(0, 0), new Dimension(w, h));
            compEl.boundingBox = new Box(new Position(x, y), new Dimension(w, h));

            elements.push(compEl);
        });

        return elements;
    }

    createCopy(): TranscriptElement {
        const copy = new TranscriptElement(this.toObject());
        copy._metrics = this._metrics;
        copy._hasCustomFontSize = this._hasCustomFontSize;

        return copy;
    }

    toObject(): TranscriptElementParams {
        return {
            ...super.toObject(),
            enabled: this.enabled,
            cues: this.cues.map((cue) => ({ ...cue })),
            fontId: this.fontId,
            fontSize: this.fontSize,
            color: this.color,
            background: this.background,
            stroke: this.stroke,
            opacity: this.opacity,
            rotation: this.rotation,
            dimension: this.dimension.toObject(),
            position: this.position.toObject(),
        };
    }
}
